import React from 'react';
import { BookOpen } from '@phosphor-icons/react';

// Analytics API row shape from learn_my_active_courses metric
// Keys are camelCase because analyticsService applies snakeToCamel on the response
interface ActiveCourseRow {
    id: string | number;
    courseTitle: string;
    progressStatus: string;
    progressPercent: number;
    dueDate?: string;
    assignmentCategory?: string;
}

interface MyActiveCoursesWidgetProps {
    data?: ActiveCourseRow[] | null;
    loading?: boolean;
    error?: string;
}

const getProgressColor = (progress: number, isOverdue: boolean) => {
    if (isOverdue) return { bar: 'bg-red-500', text: 'text-red-600' };
    if (progress >= 75) return { bar: 'bg-emerald-500', text: 'text-emerald-600' };
    if (progress >= 40) return { bar: 'bg-amber-500', text: 'text-amber-600' };
    return { bar: 'bg-primary', text: 'text-primary' };
};

const getDueInfo = (dueDate?: string) => {
    if (!dueDate) return null;
    const now = new Date();
    const due = new Date(dueDate);
    const diffDays = Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return { label: `Overdue by ${Math.abs(diffDays)} days`, className: 'text-red-500 font-medium', isOverdue: true };
    if (diffDays <= 7) return { label: `Due in ${diffDays} days`, className: 'text-red-500', isOverdue: false };
    const formatted = due.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return { label: `Due: ${formatted}`, className: 'text-gray-400', isOverdue: false };
};

const getCategoryBadge = (category?: string) => {
    if (!category) return null;
    const lower = category.toLowerCase();
    if (lower === 'mandatory') return { label: 'Mandatory', className: 'bg-red-100 text-red-700' };
    return { label: 'Optional', className: 'bg-blue-100 text-blue-700' };
};

export const MyActiveCoursesWidget: React.FC<MyActiveCoursesWidgetProps> = ({ data, loading, error }) => {
    const courses = Array.isArray(data) ? data : [];
    const activeCourses = courses.filter(c => c.progressStatus !== 'completed');

    return (
        <div className="flex flex-col h-full">
            <div className="px-4 pt-4 pb-4 flex items-center justify-between">
                <span className="text-xs text-gray-400">{activeCourses.length} in progress</span>
            </div>
            <div className="flex-1 overflow-auto px-4 pb-4">
                {loading ? (
                    <div className="flex items-center justify-center h-full">
                        <div className="w-6 h-6 border-3 border-gray-200 border-t-primary rounded-full animate-spin" />
                    </div>
                ) : error ? (
                    <div className="flex items-center justify-center h-full text-red-500 text-sm">{error}</div>
                ) : activeCourses.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2">
                        <BookOpen className="w-8 h-8" />
                        <span className="text-sm">No active courses</span>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {activeCourses.map((course) => {
                            const progress = Number(course.progressPercent) || 0;
                            const dueInfo = getDueInfo(course.dueDate);
                            const isOverdue = dueInfo?.isOverdue ?? false;
                            const colors = getProgressColor(progress, isOverdue);
                            const badge = getCategoryBadge(course.assignmentCategory);
                            return (
                                <div key={course.id}>
                                    <div className="flex items-center justify-between mb-1.5">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm text-gray-800">{course.courseTitle}</span>
                                            {badge && (
                                                <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${badge.className}`}>{badge.label}</span>
                                            )}
                                        </div>
                                        <span className={`text-xs font-medium ${colors.text}`}>{progress}%</span>
                                    </div>
                                    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                                        <div className={`h-full ${colors.bar} rounded-full transition-all duration-500`} style={{ width: `${progress}%` }} />
                                    </div>
                                    {dueInfo && (
                                        <div className="flex justify-end mt-1">
                                            <span className={`text-[11px] ${dueInfo.className}`}>{dueInfo.label}</span>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};
